// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
export class CognitoError extends Error {
    /** Cognito's exception name, e.g. "UsernameExistsException". */
    code;
    constructor(code, message) {
        super(message);
        this.name = "CognitoError";
        this.code = code;
    }
}
async function idpFetch(config, action, payload, fetchImpl) {
    const response = await fetchImpl(`https://cognito-idp.${config.region}.amazonaws.com/`, {
        method: "POST",
        headers: {
            "Content-Type": "application/x-amz-json-1.1",
            "X-Amz-Target": `AWSCognitoIdentityProviderService.${action}`,
        },
        body: JSON.stringify(payload),
    });
    const json = (await response.json().catch(() => ({})));
    if (!response.ok) {
        const errorBody = json;
        const code = errorBody.__type?.split("#").pop() ?? "UnknownError";
        throw new CognitoError(code, errorBody.message ?? `Cognito ${action} request failed`);
    }
    return json;
}
function mapAuthenticationResult(result, fallbackRefreshToken) {
    if (!result) {
        throw new CognitoError("ChallengeRequired", "Cognito returned a challenge this client does not support yet.");
    }
    const refreshToken = result.RefreshToken ?? fallbackRefreshToken;
    if (!refreshToken) {
        throw new CognitoError("MissingRefreshToken", "Cognito response did not include a refresh token.");
    }
    return {
        idToken: result.IdToken,
        accessToken: result.AccessToken,
        refreshToken,
        expiresInSeconds: result.ExpiresIn,
        tokenType: result.TokenType,
    };
}
export function createCognitoClient(config, fetchImpl = fetch) {
    return {
        async signUp(email, password) {
            const json = await idpFetch(config, "SignUp", {
                ClientId: config.clientId,
                Username: email,
                Password: password,
                UserAttributes: [{ Name: "email", Value: email }],
            }, fetchImpl);
            return { userConfirmed: Boolean(json.UserConfirmed) };
        },
        async confirmSignUp(email, confirmationCode) {
            await idpFetch(config, "ConfirmSignUp", { ClientId: config.clientId, Username: email, ConfirmationCode: confirmationCode }, fetchImpl);
        },
        async resendConfirmationCode(email) {
            await idpFetch(config, "ResendConfirmationCode", { ClientId: config.clientId, Username: email }, fetchImpl);
        },
        async signIn(email, password) {
            const json = await idpFetch(config, "InitiateAuth", {
                AuthFlow: "USER_AUTH",
                ClientId: config.clientId,
                AuthParameters: {
                    USERNAME: email,
                    PASSWORD: password,
                    PREFERRED_CHALLENGE: "PASSWORD",
                },
            }, fetchImpl);
            return mapAuthenticationResult(json.AuthenticationResult);
        },
        async refresh(refreshToken) {
            const json = await idpFetch(config, "InitiateAuth", {
                AuthFlow: "REFRESH_TOKEN_AUTH",
                ClientId: config.clientId,
                AuthParameters: { REFRESH_TOKEN: refreshToken },
            }, fetchImpl);
            return mapAuthenticationResult(json.AuthenticationResult, refreshToken);
        },
        async forgotPassword(email) {
            await idpFetch(config, "ForgotPassword", { ClientId: config.clientId, Username: email }, fetchImpl);
        },
        async confirmForgotPassword(email, confirmationCode, newPassword) {
            await idpFetch(config, "ConfirmForgotPassword", {
                ClientId: config.clientId,
                Username: email,
                ConfirmationCode: confirmationCode,
                Password: newPassword,
            }, fetchImpl);
        },
        async signOut(refreshToken) {
            // Best-effort: revoke server-side tokens, but signing out locally
            // must never depend on this succeeding (network may be gone).
            // RevokeToken, NOT GlobalSignOut: the owner tests web and mobile in
            // parallel with one account, and GlobalSignOut revoked EVERY device's
            // refresh token — each web sign-out silently forced a full
            // email+password re-login on the phone (live incident 2026-08-30).
            // Signing out one client must only ever end that client's session.
            try {
                await idpFetch(config, "RevokeToken", { Token: refreshToken, ClientId: config.clientId }, fetchImpl);
            }
            catch {
                // Local session state is cleared by the caller regardless.
            }
        },
    };
}
